import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SimplePoetSelector from "@/components/SimplePoetSelector";
import GeneratedPoemCard from "@/components/GeneratedPoemCard";
import { getAllPoets } from "@/services/poetService";
import { generateAndSavePoem } from "@/services/poemService";
import { Loader2, FileText } from "lucide-react";
import type { Database } from '@/types/supabase';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import HighlightGenerationStatus from '@/components/highlight/HighlightGenerationStatus';
import { useHighlightGeneration } from '@/hooks/useHighlightGeneration';
import { useAuth } from "@/context/AuthContext";

type Poet = Database['public']['Tables']['poets']['Row'];

// List of available subjects
const SUBJECTS = [
  "Moose", 
  "Magnesium", 
  "Marriage"
];

// Properly debounced search hook with debugging
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    console.log("[Debounce] Input value changed:", value);
    
    // Only set up the timeout if we have a value
    const handler = setTimeout(() => {
      console.log("[Debounce] Setting debounced value:", value);
      setDebouncedValue(value);
    }, delay);

    return () => {
      console.log("[Debounce] Cleanup timer for value:", value);
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Generation step states for more detailed loading indicators
enum GenerationStep {
  IDLE = 'idle',
  PREPARING = 'preparing',
  GENERATING_POEM = 'generating_poem',
  GENERATING_TITLE = 'generating_title',
  SAVING = 'saving',
  COMPLETE = 'complete',
  ERROR = 'error'
}

const Generate: React.FC = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [poets, setPoets] = useState<Poet[]>([]);
  const [selectedPoet, setSelectedPoet] = useState<Poet | null>(null);
  const [subject, setSubject] = useState<string>("Moose");
  const [loadingStep, setLoadingStep] = useState<GenerationStep>(GenerationStep.IDLE);
  const [error, setError] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isLoadingPoets, setIsLoadingPoets] = useState(true);
  const [generatedPoem, setGeneratedPoem] = useState<any | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  
  const loading = loadingStep !== GenerationStep.IDLE && 
                 loadingStep !== GenerationStep.COMPLETE && 
                 loadingStep !== GenerationStep.ERROR;
  
  // Add logging to state updates
  const updateSearchQuery = (query: string) => {
    console.log("[Generate] Setting search query:", query);
    setSearchQuery(query);
  };
  
  // Debounce the search query to avoid excessive filtering
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Add logging when debounced value changes
  useEffect(() => {
    console.log("[Generate] Debounced search query updated:", debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  // Load poets when component mounts
  useEffect(() => {
    console.log("[Generate] Component mounted, loading poets");
    loadPoets();
    
    // Load saved state from localStorage
    loadStateFromLocalStorage();
  }, []);
  
  // Save state to localStorage whenever it changes
  useEffect(() => {
    // Only save when we have a valid state to save (selected poet) and we're not in a loading state
    if (selectedPoet && !loading) {
      saveStateToLocalStorage();
    }
  }, [selectedPoet, subject, generatedPoem, loading]);
  
  // Function to save current state to localStorage
  const saveStateToLocalStorage = () => {
    try {
      const stateToSave = {
        selectedPoet: selectedPoet ? {
          id: selectedPoet.id,
          name: selectedPoet.name,
          // Only save essential poet data to keep storage size small
        } : null,
        subject,
        generatedPoem,
        lastUpdated: new Date().toISOString()
      };
      
      localStorage.setItem('mooseVerseGenerateState', JSON.stringify(stateToSave));
      console.log("[Generate] Saved state to localStorage");
    } catch (error) {
      console.error("[Generate] Error saving state to localStorage:", error);
      // Don't show error to user as this is a non-critical feature
    }
  };
  
  // Function to load saved state from localStorage
  const loadStateFromLocalStorage = () => {
    try {
      const savedState = localStorage.getItem('mooseVerseGenerateState');
      
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        console.log("[Generate] Loaded state from localStorage:", parsedState);
        
        // Check if the saved state is still relevant (not older than 24 hours)
        const lastUpdated = new Date(parsedState.lastUpdated || 0);
        const isStateExpired = Date.now() - lastUpdated.getTime() > 24 * 60 * 60 * 1000;
        
        if (isStateExpired) {
          console.log("[Generate] Saved state is expired, not restoring");
          localStorage.removeItem('mooseVerseGenerateState');
          return;
        }
        
        // Restore subject (simplest to restore)
        if (parsedState.subject && SUBJECTS.includes(parsedState.subject)) {
          setSubject(parsedState.subject);
        }
        
        // Restore selected poet (need to find the poet in the loaded poets)
        if (parsedState.selectedPoet) {
          // We'll restore the selected poet after poets are loaded
          const savedPoetData = parsedState.selectedPoet;
          
          // Find matching poet in loaded poets
          const matchingPoet = poets.find(p => p.id === savedPoetData.id);
          
          if (matchingPoet) {
            setSelectedPoet(matchingPoet);
          }
        }
        
        // Restore generated poem
        if (parsedState.generatedPoem) {
          setGeneratedPoem(parsedState.generatedPoem);
        }
      } else {
        console.log("[Generate] No saved state found in localStorage");
      }
    } catch (error) {
      console.error("[Generate] Error loading state from localStorage:", error);
      // Silently fail and continue with default state
    }
  };
  
  // Need to handle restoring poet after poets are loaded
  useEffect(() => {
    // Only try to restore poet selection if poets have been loaded
    if (!isLoadingPoets && poets.length > 0) {
      try {
        const savedState = localStorage.getItem('mooseVerseGenerateState');
        
        if (savedState) {
          const parsedState = JSON.parse(savedState);
          
          if (parsedState.selectedPoet && !selectedPoet) {
            const savedPoetId = parsedState.selectedPoet.id;
            const matchingPoet = poets.find(p => p.id === savedPoetId);
            
            if (matchingPoet) {
              console.log("[Generate] Restoring selected poet:", matchingPoet.name);
              setSelectedPoet(matchingPoet);
            }
          }
        }
      } catch (error) {
        console.error("[Generate] Error restoring poet after poets loaded:", error);
      }
    }
  }, [poets, isLoadingPoets, selectedPoet]);

  const loadPoets = async () => {
    try {
      setIsLoadingPoets(true);
      setFetchError(null);
      
      // Fetch poets from database
      console.log("[Generate] Fetching poets...");
      const allPoets = await getAllPoets();
      console.log(`[Generate] Fetched ${allPoets.length} poets`);
      setPoets(allPoets);
    } catch (err) {
      console.error("[Generate] Error loading poets:", err);
      setFetchError("Failed to load poets. Please try again later.");
      toast({
        title: "Error",
        description: "Failed to load poets. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingPoets(false);
    }
  };

  // Filter poets based on debounced search query in a memoized way
  const filteredPoets = useMemo(() => {
    try {
      console.log("[Generate] Filtering poets with query:", debouncedSearchQuery);
      console.log("[Generate] Number of poets to filter:", poets.length);
      
      // Return a small default set if no query or query too short
      if (!debouncedSearchQuery || debouncedSearchQuery.length < 2) {
        const defaultPoets = poets.slice(0, 10).sort((a, b) => a.name.localeCompare(b.name));
        console.log("[Generate] Returning default poets list:", defaultPoets.length);
        return defaultPoets;
      }

      const lowerCaseQuery = debouncedSearchQuery.toLowerCase();
      
      // Create a more efficient filtering approach
      const startsWith = [];
      const includes = [];
      
      // Limited number for performance
      for (let i = 0; i < poets.length && (startsWith.length + includes.length) < 100; i++) {
        const poet = poets[i];
        const poetName = poet.name.toLowerCase();
        
        if (poetName.startsWith(lowerCaseQuery)) {
          startsWith.push(poet);
        } else if (poetName.includes(lowerCaseQuery)) {
          includes.push(poet);
        }
      }
      
      const filteredResult = [...startsWith, ...includes];
      
      console.log(`[Generate] Filtered to ${filteredResult.length} poets`);
      return filteredResult.slice(0, 100);
    } catch (error) {
      console.error("Error filtering poets:", error);
      return []; // Return empty array on error
    }
  }, [poets, debouncedSearchQuery]);

  // Safely handle search input
  const handleSearch = useCallback((query: string) => {
    console.log("[Generate] Search query changed:", query);
    updateSearchQuery(query);
  }, []);

  // Get loading message based on current step
  const getLoadingMessage = () => {
    switch (loadingStep) {
      case GenerationStep.PREPARING:
        return "Preparing to generate your poem...";
      case GenerationStep.GENERATING_POEM:
        return `Creating a poem about ${subject}, inspired by the style of ${selectedPoet?.name}...`;
      case GenerationStep.GENERATING_TITLE:
        return "Creating a title for your poem...";
      case GenerationStep.SAVING:
        return "Saving your poem...";
      default:
        return `Generating your poem...`;
    }
  };
  
  // Button text based on loading state
  const getButtonText = () => {
    // For IDLE or COMPLETE states, show the standard button text
    if (loadingStep === GenerationStep.IDLE || 
        loadingStep === GenerationStep.COMPLETE || 
        loadingStep === GenerationStep.ERROR) {
      return "Create a New Poem!";
    }
    
    // Show the appropriate loading text based on step
    switch (loadingStep) {
      case GenerationStep.PREPARING:
        return "Preparing...";
      case GenerationStep.GENERATING_POEM:
        return "Generating...";
      case GenerationStep.GENERATING_TITLE:
        return "Creating title...";
      case GenerationStep.SAVING:
        return "Saving...";
      default:
        return "Create a New Poem!";
    }
  };

  const {
    status: highlightGenerationStatus,
    error: highlightGenerationError,
    highlightCount,
    clearStatus: clearHighlightStatus
  } = useHighlightGeneration('generate');

  const handleGeneratePoem = async () => {
    if (!selectedPoet) {
      toast({
        variant: "destructive",
        title: "Choose a Poet",
        description: "Please select a poet before generating a poem.",
      });
      return;
    }
    
    // --- Calculate token limit percentage --- 
    const randomRoll = Math.floor(Math.random() * 20) + 1; 
    let tokenLimitPercentage: number;
    if (randomRoll === 20) tokenLimitPercentage = 1.0;
    else if (randomRoll === 19) tokenLimitPercentage = 0.9;
    else if (randomRoll === 18) tokenLimitPercentage = 0.8;
    else if (randomRoll === 17) tokenLimitPercentage = 0.7;
    else if (randomRoll >= 15) tokenLimitPercentage = 0.6;
    else if (randomRoll >= 12) tokenLimitPercentage = 0.5;
    else if (randomRoll >= 8) tokenLimitPercentage = 0.4;
    else if (randomRoll >= 4) tokenLimitPercentage = 0.3;
    else if (randomRoll === 3) tokenLimitPercentage = 0.25;
    else if (randomRoll === 2) tokenLimitPercentage = 0.2;
    else tokenLimitPercentage = 0.15; // randomRoll === 1
    console.log(`[Generate] Random roll: ${randomRoll}, Token Limit Percentage: ${tokenLimitPercentage}`);
    // --- End Calculate token limit percentage --- 

    try {
      setLoadingStep(GenerationStep.PREPARING);
      setError(null);
      setGeneratedPoem(null);
      
      setTimeout(async () => {
        try {
          setLoadingStep(GenerationStep.GENERATING_POEM);
          console.log(`Starting poem generation for poet: ${selectedPoet.name} on subject: ${subject}`);
          clearHighlightStatus();
          
          // Pass tokenLimitPercentage in options
          try {
            const poemResult = await generateAndSavePoem(
              selectedPoet.id,
              selectedPoet.name,
              subject,
              user?.id ?? null,
              { tokenLimitPercentage: tokenLimitPercentage }
            );
            
            // Avoid logging the full AI payload in the browser; keep it on the server only
            if (typeof window === 'undefined') {
              console.log("Poem generation complete:", {
                poemId: poemResult && 'data' in poemResult && poemResult.data ? (poemResult.data as any).id : undefined,
              });
            }
            
            // Process both direct poem data and ServiceResult format
            const poemDetails = poemResult && 'data' in poemResult ? poemResult.data : poemResult;
            
            // Validate response to ensure we have proper data
            if (!poemDetails || 
                typeof poemDetails !== 'object' || 
                typeof (poemDetails as any).content !== 'string' || 
                typeof (poemDetails as any).title !== 'string') {
              console.error("Invalid poem data received:", poemDetails);
              setError("Generated poem data is incomplete. Please try again.");
              setLoadingStep(GenerationStep.ERROR);
              toast({
                title: "Generation Error",
                description: "The generated poem data was incomplete. Please try again.",
                variant: "destructive",
              });
              return;
            }
            
            // Add the selected poet's name to the poemDetails to ensure proper attribution
            const enrichedPoemDetails = {
              ...poemDetails,
              poet_name: selectedPoet.name, // Explicitly add the poet name to the data
              style_description: selectedPoet.style_description, // Add style description
              poetry_foundation_url: selectedPoet.poetry_foundation_url, // Add Poetry Foundation URL
              style_points: selectedPoet.style_points // Add style points if available
            };
            
            setGeneratedPoem(enrichedPoemDetails);
            setLoadingStep(GenerationStep.COMPLETE);
          } catch (err) {
            console.error("Error during poem generation:", err);
            setLoadingStep(GenerationStep.ERROR);
            
            // Extract a user-friendly error message
            let errorMessage = "Failed to generate poem due to AI service error";
            
            // Check for specific API errors
            if (err instanceof Error) {
              if (err.message.includes('Rate limit')) {
                errorMessage = "The poem generation service is experiencing high demand. Please try again in a few minutes.";
              } else if (err.message.includes('billing')) {
                errorMessage = "The poem generation service is temporarily unavailable due to account limitations.";
              } else if (err.message.includes('context_length_exceeded')) {
                errorMessage = "Your poem request was too complex. Please try a different poet or subject.";
              } else if (err.message.includes('API key') || err.message.includes('authentication')) {
                errorMessage = "The poem service is experiencing authentication issues. Please try again later.";
              } else if (err.message.includes('timeout') || err.message.includes('timed out')) {
                errorMessage = "The poem generation took too long to complete. Please try again.";
              } else if (err.message.includes('project-scoped')) {
                errorMessage = "The AI model is currently unavailable. Please try again later or select a different poet.";
              }
            }
            
            setError(errorMessage);
            
            toast({
              title: "Generation Error",
              description: errorMessage,
              variant: "destructive",
            });
          }
        } catch (err) {
          console.error("Outer error in poem generation:", err);
          setLoadingStep(GenerationStep.ERROR);
          setError("An unexpected error occurred during poem generation. Please try again.");
          
          toast({
            title: "System Error",
            description: "An unexpected error occurred during poem generation. Please try again.",
            variant: "destructive",
          });
        }
      }, 100);
    } catch (err: any) {
      console.error("Setup error:", err);
      setError(err.message || "Failed to start generation process");
      setLoadingStep(GenerationStep.ERROR);
      toast({
        title: "Error",
        description: err.message || "Failed to start generation process",
        variant: "destructive",
      });
    }
  };

  // Function to clear saved state
  const clearSavedState = () => {
    try {
      localStorage.removeItem('mooseVerseGenerateState');
      setSelectedPoet(null);
      setSubject("Moose");
      setGeneratedPoem(null);
      setError(null);
      setLoadingStep(GenerationStep.IDLE);
      toast({
        title: "State Reset",
        description: "Your saved poem generation settings have been cleared.",
      });
      console.log("[Generate] Cleared saved state");
    } catch (error) {
      console.error("[Generate] Error clearing saved state:", error);
    }
  };

  return (
    <div className="min-h-screen pt-16" data-testid="generate-component">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="flex justify-between items-center mb-8">
          <h1 className="font-poetry text-4xl font-semibold">Create a Poem</h1>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
          
          {/* Column 1: Inputs and Generation Button */}
          <div className="border rounded-md p-4 bg-card space-y-8 book-shadow self-start">
              {/* Instructions Box (Adjust internal padding if needed due to parent padding) */}
              <div className="flex items-center gap-4 p-0 bg-muted/50 rounded-md border-none mb-2"> 
                <img 
                  src="/images/lowresmoose.png" 
                  alt="Moose" 
                  className="w-24 h-auto rounded-md"
                />
                <div className="text-base space-y-2">
                  <p>Choose a poet whose style will inspire your poem, then choose a subject and click the button for a new poem.</p>
                  <p>Moose poems can have three poems at most inspired by the style of a given poet on a given subject. When you generate a fourth poem, the oldest existing poem is removed.</p>
                  <p>Poem generation has some limits. If it's not available, please try again later.</p>
                </div>
              </div>

              <div>
                <label htmlFor="poet" className="block text-sm font-medium mb-2">
                  Choose a poet whose style will inspire your new poem
                </label>
                <SimplePoetSelector
                  poets={filteredPoets}
                  selectedPoet={selectedPoet}
                  onSelectPoet={setSelectedPoet}
                  isLoading={isLoadingPoets}
                  onSearch={handleSearch}
                  placeholder="Search for a poet..."
                  className="w-full"
                  data-testid="poet-selector"
                />
                <div className="flex items-center justify-between mt-1">
                  <p className="text-sm text-muted-foreground">
                    {isLoadingPoets 
                      ? "Loading poets..." 
                      : fetchError 
                        ? "Error loading poets" 
                        : `${poets.length} poets available`}
                  </p>
                  <div className="flex space-x-2">
                    {fetchError && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={loadPoets}
                        className="text-xs"
                      >
                        Retry
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium mb-2">
                  Choose a Subject
                </label>
                <Select
                  value={subject}
                  onValueChange={setSubject}
                  data-testid="subject-selector"
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {SUBJECTS.map((subj) => (
                      <SelectItem key={subj} value={subj}>
                        {subj}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-4">
                <Button 
                  onClick={handleGeneratePoem} 
                  disabled={loading || !selectedPoet || isLoadingPoets || !!fetchError}
                  className="w-full py-6 text-lg transition-colors hover:bg-primary/90 active:bg-primary/70 active:scale-[0.99] hover:shadow-md active:shadow"
                  data-testid="generate-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    </>
                  ) : (
                    getButtonText()
                  )}
                </Button>
              </div>
          </div>
          
          {/* Column 2: Generated Output, Loading, Error */}
          <div className="space-y-4 self-start">
            {/* Generated Poem Card (conditionally rendered) */}
            {generatedPoem && !loading && loadingStep !== GenerationStep.ERROR && (
              <React.Fragment>
                <GeneratedPoemCard poem={generatedPoem} />
                <HighlightGenerationStatus 
                    status={highlightGenerationStatus} 
                    error={highlightGenerationError} 
                    highlightCount={highlightCount} 
                    context="generate"
                />
              </React.Fragment>
            )}

            {/* Loading Indicator - Wrapped for alignment */}
            {loading && (
              <div className="border rounded-md p-4 bg-card text-center py-12" data-testid="loading-indicator">
                <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-muted-foreground" />
                <p className="text-lg text-muted-foreground">
                  {getLoadingMessage()}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  This may take a moment...
                </p>
              </div>
            )}

            {/* Poet Loading Error Message (Specific) - Wrapped for alignment */}
            {fetchError && !isLoadingPoets && (
              <div className="border rounded-md p-4 bg-destructive/10 border-destructive text-destructive" data-testid="error-message">
                <h3 className="font-semibold mb-2">Error Loading Poets</h3>
                <p>{fetchError}</p> 
                <div className="flex space-x-2">
                  {fetchError && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={loadPoets}
                      className="text-xs"
                    >
                      Retry
                    </Button>
                  )}
                </div>
              </div>
            )}
            {/* Generation Error Message - Wrapped for alignment */}
            {error && !loading && loadingStep === GenerationStep.ERROR && (
              <div className="border rounded-md p-4 bg-destructive/10 border-destructive text-destructive" data-testid="error-message">
                <h3 className="font-semibold mb-2">Generation Error</h3>
                <p>{error}</p>
                <div className="flex space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={clearSavedState}
                    className="text-xs"
                  >
                    Try Again
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Generate;